// @flow

import React from 'react';
import _ from 'lodash';
import bemify from 'bemify-js';

import {
  type IconNameType,
  LazyLoadComponent,
} from '@zapier/common-components';

import DropdownMenu from 'app/common/components/DropdownMenu';
import Menu from 'app/common/components/Menu';
import ZapFolderChooserContainer from 'app/dashboard/components/ZapFolderChooserContainer';
import { canMoveZapToFolder } from 'app/dashboard/utils/DashboardUtils';
import 'app/dashboard/components/ZapMenu.scss';

import type { Element } from 'react';
import type { ZapSummary } from '@zapier/design-system-zap-sharing';

import type { FolderIdType } from 'app/dashboard/types';

const ZapSharingModal = LazyLoadComponent({
  loader: () =>
    import(
      /* webpackChunkName: 'app-shared-zap-ShareZapModalWithData' */ 'app/shared-zap/components/ShareZapModalWithData'
    ),
});

const bem = bemify('zap-menu');

export type Props = {
  canShareGlobally: boolean,
  canSharePrivately: boolean,
  canSharePublicly: boolean,
  folders: Array<{ id: number }>,
  isFolderShare: boolean,
  isInHeldFolder?: boolean,
  isInTrash?: boolean,
  isOwner: boolean,
  isPaused: boolean,
  isZapShared: boolean,
  onClose: () => void,
  onConfirmDelete: () => void,
  onConfirmMove: (folderId: number) => void,
  onCopy: () => void,
  onEdit: () => void,
  onRename: () => void,
  onRun?: () => void,
  onTransfer: () => void,
  onViewHistory: () => void,
  ownFolderIds: Array<FolderIdType>,
  title: ?string,
  track: (message: string, data: *) => void,
  zapSummary: ZapSummary,
};

type ZapMenuProps = {
  ...$Exact<Props>,
  isConfirmingDeletion: boolean,
  isMovingToFolder: boolean,
  isSharingPublicly: boolean,
  onDelete: () => void,
  onMove: () => void,
  onSharePublicly: () => void,
};

type ZapMenuItem = {
  label: (props?: *) => string,
  icon: IconNameType,
  shouldShow: (props: *) => boolean,
  onChoose: (props: *) => *,
};

const mainMenuConfig: {
  shouldRender: (props?: *) => boolean,
  MenuWrapper: (props: *) => Element<*>,
  items: Array<ZapMenuItem>,
} = {
  shouldRender: () => true,
  MenuWrapper: props => {
    return (
      <Menu
        className={props.className}
        items={mainMenuConfig.items
          .filter(item => item.shouldShow(props))
          .map(item => ({
            ...item,
            icon: item.icon,
            label: item.label(props),
          }))}
        listClassName={props.listClassName}
        onChoose={props.onChoose}
      />
    );
  },
  items: [
    {
      label: () => 'Run',
      value: 'run',
      icon: 'zap-arrow',
      shouldShow: props => !props.isInTrash && !!props.onRun,
      onChoose: props => props.onRun(),
    },
    {
      label: () => 'Edit',
      value: 'edit',
      icon: 'edit',
      shouldShow: props => !props.isInTrash,
      onChoose: props => props.onEdit(),
    },
    {
      label: () => 'Rename',
      value: 'rename',
      icon: 'custom-value',
      shouldShow: props => !props.isInTrash,
      onChoose: props => props.onRename(),
    },
    {
      label: () => 'Transfer',
      value: 'transfer',
      icon: 'move',
      shouldShow: props => props.isFolderShare || props.isZapShared,
      onChoose: props => props.onTransfer(),
    },
    {
      label: props =>
        props && props.isZapShared ? 'Sharing Permissions…' : 'Share Zap',
      value: 'share',
      icon: 'team',
      shouldShow: props =>
        props.canShareGlobally ||
        props.canSharePrivately ||
        props.canSharePublicly,
      onChoose: props => props.onSharePublicly(),
    },
    {
      label: () => 'Move to Folder…',
      value: 'move',
      icon: 'folder',
      shouldShow: props => {
        return (
          !props.isInHeldFolder &&
          props.folders.filter(folder => {
            return canMoveZapToFolder(
              {
                isOwnerOfZap: props.isOwner,
                zapFolderIds: props.ownFolderIds,
              },
              folder
            );
          }).length > 1
        );
      },
      onChoose: props => props.onMove(),
    },
    {
      label: () => 'Task History',
      value: 'history',
      icon: 'reload',
      shouldShow: () => true,
      onChoose: props => props.onViewHistory(),
    },
    {
      label: () => 'Copy',
      value: 'copy',
      icon: 'copy',
      shouldShow: props => !props.isInTrash && !props.isInHeldFolder,
      onChoose: props => props.onCopy(),
    },
    {
      label: () => 'Trash',
      value: 'delete',
      icon: 'trash',
      shouldShow: props =>
        !props.isConfirmingDeletion &&
        !props.isInTrash &&
        !props.isInHeldFolder,
      onChoose: props => props.onDelete(),
    },
    {
      label: props =>
        props && props.isPaused ? 'Really move to trash?' : 'Zap must be OFF',
      value: 'confirmDelete',
      icon: 'trash',
      shouldShow: props => props.isConfirmingDeletion,
      onChoose: props => (props.isPaused ? props.onConfirmDelete() : () => {}),
    },
  ],
};

const moveToFolderConfig = {
  shouldRender: (props: ZapMenuProps) => props.isMovingToFolder,
  MenuWrapper: props => {
    return (
      <ZapFolderChooserContainer
        className={props.className}
        onChoose={props.onChoose}
        zapId={props.id}
      />
    );
  },
  onChoose: (props, value) => props.onConfirmMove(value),
};

const sharePubliclyConfig = {
  shouldRender: (props: ZapMenuProps) => props.isSharingPublicly,
  MenuWrapper: props => (
    <ZapSharingModal
      canShareGlobally={props.canShareGlobally}
      canSharePrivately={props.canSharePrivately}
      canSharePublicly={props.canSharePublicly}
      location="dashboard"
      onClose={props.onClose}
      track={props.track}
      zapSummary={props.zapSummary}
    />
  ),
};

const ZapMenu = (props: ZapMenuProps) => {
  const config =
    [sharePubliclyConfig, moveToFolderConfig, mainMenuConfig].find(conf =>
      conf.shouldRender(props)
    ) || {};
  const onChoose = (value, didConfirm, item) => {
    // $FlowFixMe
    const onChooseItem = item.onChoose || config.onChoose;
    // `item.onChoose` should always be a function, but
    // in case something's off let's not completely break
    if (didConfirm && _.isFunction(onChooseItem)) {
      onChooseItem(props, value);
    }
  };

  return (
    <DropdownMenu
      {...props}
      className={bem()}
      kind="below justify-right"
      listClassName={bem('__list')}
      Menu={config.MenuWrapper}
      onChoose={onChoose}
      // don't auto-close when the user is working
      // in the zap sharing modal, because it
      // handles its own close events.
      onClickOutside={props.isSharingPublicly ? null : props.onClose}
    />
  );
};

export default ZapMenu;
